const SUPPORT_AI_OPERATIONS_READ = 'support.ai.operations.read' as const;
const SUPPORT_AI_REPLAY_EXECUTION = 'support.ai.evaluation.replay' as const;
const SUPPORT_AI_CUSTOMER_READINESS = 'support.ai.customer_readiness.review' as const;
const SUPPORT_AI_ROLLOUT_CONTROL = 'support.ai.rollout.control' as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isSafeRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  try {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}

function ownDataProperty(value: Record<string, unknown>, key: string): unknown {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    return descriptor && descriptor.enumerable === true && 'value' in descriptor
      ? descriptor.value
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Project the one capability needed by the Operations workspace.
 * Missing, stale, unavailable, or malformed context is always denied.
 */
export function hasSupportAIOperationsAccess(value: unknown): boolean {
  if (!isSafeRecord(value) || ownDataProperty(value, 'available') !== true) return false;
  const capabilities = ownDataProperty(value, 'capabilities');
  if (!isSafeRecord(capabilities)) return false;
  const capability = ownDataProperty(capabilities, SUPPORT_AI_OPERATIONS_READ);
  return (
    isSafeRecord(capability) &&
    ownDataProperty(capability, 'granted') === true &&
    ownDataProperty(capability, 'risk_level') === 'read' &&
    ownDataProperty(capability, 'step_up_required') === false
  );
}

export function hasSupportAIReplayExecutionAccess(value: unknown): boolean {
  if (!isRecord(value) || value.available !== true || !isRecord(value.capabilities)) return false;

  const capability = value.capabilities[SUPPORT_AI_REPLAY_EXECUTION];
  return (
    isRecord(capability) &&
    capability.granted === true &&
    capability.risk_level === 'write' &&
    capability.step_up_required === false
  );
}

export function hasSupportAICustomerReadinessAccess(value: unknown): boolean {
  if (!isSafeRecord(value) || ownDataProperty(value, 'available') !== true) return false;
  const capabilities = ownDataProperty(value, 'capabilities');
  if (!isSafeRecord(capabilities)) return false;
  const capability = ownDataProperty(capabilities, SUPPORT_AI_CUSTOMER_READINESS);
  return (
    isSafeRecord(capability) &&
    ownDataProperty(capability, 'granted') === true &&
    ownDataProperty(capability, 'risk_level') === 'dangerous' &&
    ownDataProperty(capability, 'step_up_required') === true
  );
}

export function hasSupportAIRolloutControlAccess(
  context: Api.Support.AIGovernanceCapabilityContext | null | undefined
): boolean {
  if (!isSafeRecord(context) || ownDataProperty(context, 'available') !== true) return false;
  const capabilities = ownDataProperty(context, 'capabilities');
  if (!isSafeRecord(capabilities)) return false;
  const capability = ownDataProperty(capabilities, SUPPORT_AI_ROLLOUT_CONTROL);
  return (
    isSafeRecord(capability) &&
    ownDataProperty(capability, 'granted') === true &&
    ownDataProperty(capability, 'risk_level') === 'dangerous' &&
    ownDataProperty(capability, 'step_up_required') === true
  );
}
